import { useTranslation } from 'next-i18next'
import { Pressable, Text, TextInput, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { P, match } from 'ts-pattern'

import { AirpodsCouponSection } from '@/shared/components/flows/AirpodsCampaign'
import { isAirpodsCoupon } from '@/utils/airpodsCampaign'
import type { PremiumInterval } from '@flows-ui/services/QuoteDataContext.types'
import type { AppliedDiscount } from '@flows-ui/utils/discounts'
import type { ProductExtension } from '@flows/index'
import { body1, body2, caption, h4 } from '@ui-kit/core/typography'

const orderSummaryStyles = tv({
  slots: {
    card: 'rounded-2xl border border-grey-200 bg-grey-100 px-6 py-6',
    title: [h4({ type: 'secondary' }), 'pb-4 text-left'],
    row: 'flex-row justify-between py-2',
    label: body2({ color: 'grey700' }),
    value: body2({ bold: true }),
    priceLabel: body1({ color: 'black', bold: true }),
    priceValue: body1({ bold: true }),
    boldLabel: body1({ color: 'black', bold: true }),
    boldValue: body1({ bold: true }),
    discountValue: body2({ color: 'success', bold: true }),
    divider: 'my-1 border-t border-grey-200',
    smallLabel: body2({ color: 'grey700' }),
    smallValue: body2({ bold: true }),
    successBanner: 'mt-3 flex-row items-center gap-3 rounded-xl px-4 py-3',
    successIconWrapper: 'h-10 w-10 items-center justify-center rounded-full bg-white',
    successIconInner:
      'h-6 w-6 items-center justify-center rounded-full border-[1.5px] border-success',
    successIcon: 'text-xs text-success',
    successText: ['flex-1', caption({ color: 'success' })].join(' '),
    couponRow: 'mt-4 flex-row items-start gap-2',
    priceRow: 'flex-row justify-between',
    couponFieldWrapper: 'flex-1 rounded-lg border border-grey-200 bg-white px-3 py-3',
    couponFloatingLabel: caption({ color: 'grey500' }),
    couponInput:
      'border-0 bg-transparent p-0 text-sm leading-6 text-black outline-none placeholder:text-grey-400',
    couponError: ['mt-1', caption({ color: 'error' })].join(' '),
    couponButton: 'items-center justify-center rounded-lg px-4 py-3',
  },
  variants: {
    couponHasError: {
      true: {
        couponFieldWrapper: 'border-error',
        couponFloatingLabel: caption({ color: 'error' }),
      },
    },
    couponActive: {
      true: {
        couponButton: 'bg-black',
      },
      false: {
        couponButton: 'bg-grey-200',
      },
    },
  },
})

interface CouponData {
  code: string
  amount: number
}

interface OrderSummaryContentProps {
  title: string
  planTitle: string
  activeExtensions: ProductExtension[]
  startDate?: string
  renewalDate?: string
  priceLabel: string
  formattedPremium: string
  priceInterval: PremiumInterval
  appliedDiscount: AppliedDiscount
  formatCurrency: (amount: number) => string
  appliedCoupon: CouponData | null
  couponCode: string
  onCouponCodeChange: (code: string) => void
  onApplyCoupon: () => void
  isCouponPending: boolean
  couponError?: string | null
}

export const OrderSummaryContent = ({
  title,
  planTitle,
  activeExtensions,
  startDate,
  renewalDate,
  priceLabel,
  formattedPremium,
  priceInterval,
  appliedDiscount,
  formatCurrency,
  appliedCoupon,
  couponCode,
  onCouponCodeChange,
  onApplyCoupon,
  isCouponPending,
  couponError,
}: OrderSummaryContentProps) => {
  const { t } = useTranslation()
  const styles = orderSummaryStyles()
  const isAirpodsCampaign = isAirpodsCoupon(appliedCoupon?.code)

  const couponInput = (
    <CouponInputSection
      priceLabel={priceLabel}
      formattedPremium={formattedPremium}
      showPrice={appliedDiscount.type === 'none'}
      showPermanentDiscountNotice={appliedDiscount.type === 'permanent'}
      couponCode={couponCode}
      onCouponCodeChange={onCouponCodeChange}
      onApplyCoupon={onApplyCoupon}
      isCouponPending={isCouponPending}
      error={couponError}
    />
  )

  return (
    <>
      <Text className={styles.title()}>{title}</Text>

      <View className={styles.row()}>
        <Text className={styles.label()}>{t('sharedFlows_common:orderSummary.plan')}</Text>
        <Text className={styles.value()}>{planTitle}</Text>
      </View>

      {activeExtensions.map((ext) => (
        <View key={ext.configurationDataExtensionKey} className={styles.row()}>
          <Text className={styles.label()}>{ext.title}</Text>
          <Text className={styles.value()}>
            {t('sharedFlows_common:orderSummary.checkout.yes')}
          </Text>
        </View>
      ))}

      {startDate && renewalDate && (
        <>
          <View className={styles.row()}>
            <Text className={styles.label()}>
              {t('sharedFlows_common:orderSummary.checkout.starting')}
            </Text>
            <Text className={styles.value()}>{startDate}</Text>
          </View>
          <View className={styles.row()}>
            <Text className={styles.label()}>
              {t('sharedFlows_common:orderSummary.checkout.renews')}
            </Text>
            <Text className={styles.value()}>{renewalDate}</Text>
          </View>
        </>
      )}

      {match({ type: appliedDiscount.type, appliedCoupon, isAirpodsCampaign })
        .with(
          { isAirpodsCampaign: true, appliedCoupon: P.nonNullable },
          ({ appliedCoupon: coupon }) => (
            <>
              <View className={styles.priceRow()}>
                <Text className={styles.priceLabel()}>{priceLabel}</Text>
                <Text className={styles.priceValue()}>
                  {formatCurrency(appliedDiscount.finalPrice)}
                </Text>
              </View>
              <AirpodsCouponSection code={coupon.code} />
            </>
          )
        )
        .with({ type: 'coupon', appliedCoupon: P.nonNullable }, ({ appliedCoupon: coupon }) => (
          <CouponAppliedSection
            appliedDiscount={appliedDiscount}
            formattedPremium={formattedPremium}
            priceInterval={priceInterval}
            priceLabel={priceLabel}
            appliedCoupon={coupon}
            formatCurrency={formatCurrency}
          />
        ))
        .with({ type: 'none', appliedCoupon: P.nonNullable }, ({ appliedCoupon: coupon }) => (
          <CouponEnrolledSection
            priceLabel={priceLabel}
            formattedPremium={formattedPremium}
            code={coupon.code}
          />
        ))
        .with({ type: P.union('permanent', 'referral') }, () => (
          <>
            <CarriedOverDiscountSection
              appliedDiscount={appliedDiscount}
              formattedPremium={formattedPremium}
              priceInterval={priceInterval}
              formatCurrency={formatCurrency}
            />
            {couponInput}
          </>
        ))
        .otherwise(() => couponInput)}
    </>
  )
}

const CouponAppliedSection = ({
  appliedDiscount,
  formattedPremium,
  priceInterval,
  priceLabel,
  appliedCoupon,
  formatCurrency,
}: {
  appliedDiscount: AppliedDiscount
  formattedPremium: string
  priceInterval: PremiumInterval
  priceLabel: string
  appliedCoupon: CouponData
  formatCurrency: (amount: number) => string
}) => {
  const { t } = useTranslation()
  const styles = orderSummaryStyles()
  const isYearly = priceInterval === 'P1Y'

  return (
    <>
      <View className={styles.row()}>
        <Text className={styles.label()}>
          {t('sharedFlows_common:orderSummary.checkout.insurancePremium')}
        </Text>
        <Text className={styles.value()}>{formattedPremium}</Text>
      </View>
      <View className={styles.row()}>
        <Text className={styles.label()}>
          {t('sharedFlows_common:orderSummary.checkout.coupon')}
        </Text>
        <Text className={styles.discountValue()}>-{formatCurrency(appliedCoupon.amount)}</Text>
      </View>
      <View className={styles.row()}>
        <Text className={styles.boldLabel()}>
          {isYearly ? t('sharedFlows_common:orderSummary.checkout.priceFirstYear') : priceLabel}
        </Text>
        <Text className={styles.boldValue()}>{formatCurrency(appliedDiscount.finalPrice)}</Text>
      </View>
      {isYearly && (
        <>
          <View className={styles.divider()} />
          <View className={styles.row()}>
            <Text className={styles.smallLabel()}>
              {t('sharedFlows_common:orderSummary.checkout.yearlyPriceAfterFirstYear')}
            </Text>
            <Text className={styles.smallValue()}>{formattedPremium}</Text>
          </View>
        </>
      )}
      <CouponSuccessBanner code={appliedCoupon.code} />
    </>
  )
}

const CouponSuccessBanner = ({ code }: { code: string }) => {
  const { t } = useTranslation()
  const styles = orderSummaryStyles()

  return (
    <View className={styles.successBanner({ className: 'bg-success-light' })}>
      <View className={styles.successIconWrapper()}>
        <View className={styles.successIconInner()}>
          <Text className={styles.successIcon()}>✓</Text>
        </View>
      </View>
      <Text className={styles.successText()}>
        {t('sharedFlows_common:orderSummary.checkout.couponSuccess', { code })}
      </Text>
    </View>
  )
}

const CouponEnrolledSection = ({
  priceLabel,
  formattedPremium,
  code,
}: {
  priceLabel: string
  formattedPremium: string
  code: string
}) => {
  const styles = orderSummaryStyles()

  return (
    <>
      <View className={styles.priceRow()}>
        <Text className={styles.priceLabel()}>{priceLabel}</Text>
        <Text className={styles.priceValue()}>{formattedPremium}</Text>
      </View>
      <CouponSuccessBanner code={code} />
    </>
  )
}

const CarriedOverDiscountSection = ({
  appliedDiscount,
  formattedPremium,
  priceInterval,
  formatCurrency,
}: {
  appliedDiscount: AppliedDiscount
  formattedPremium: string
  priceInterval: PremiumInterval
  formatCurrency: (amount: number) => string
}) => {
  const { t } = useTranslation()
  const styles = orderSummaryStyles()

  const discountLabel =
    appliedDiscount.type === 'permanent'
      ? t(`sharedFlows_common:orderSummary.checkout.permanentDiscount.${priceInterval}`)
      : t('sharedFlows_common:orderSummary.checkout.referralCredit')

  return (
    <>
      <View className={styles.row()}>
        <Text className={styles.label()}>
          {t('sharedFlows_common:orderSummary.checkout.insurancePremium')}
        </Text>
        <Text className={styles.value()}>{formattedPremium}</Text>
      </View>
      <View className={styles.row()}>
        <Text className={styles.label()}>{discountLabel}</Text>
        <Text className={styles.discountValue()}>-{formatCurrency(appliedDiscount.amount)}</Text>
      </View>
      <View className={styles.row()}>
        <Text className={styles.boldLabel()}>
          {t('sharedFlows_common:orderSummary.checkout.effectivePrice')}
        </Text>
        <Text className={styles.boldValue()}>{formatCurrency(appliedDiscount.finalPrice)}</Text>
      </View>
    </>
  )
}

const CouponInputSection = ({
  priceLabel,
  formattedPremium,
  showPrice,
  showPermanentDiscountNotice,
  couponCode,
  onCouponCodeChange,
  onApplyCoupon,
  isCouponPending,
  error,
}: {
  priceLabel: string
  formattedPremium: string
  showPrice: boolean
  showPermanentDiscountNotice: boolean
  couponCode: string
  onCouponCodeChange: (code: string) => void
  onApplyCoupon: () => void
  isCouponPending: boolean
  error?: string | null
}) => {
  const { t } = useTranslation()
  const styles = orderSummaryStyles()
  const couponStyles = orderSummaryStyles({
    couponHasError: Boolean(error),
    couponActive: Boolean(couponCode),
  })

  return (
    <>
      {showPrice && (
        <View className={styles.priceRow()}>
          <Text className={styles.priceLabel()}>{priceLabel}</Text>
          <Text className={styles.priceValue()}>{formattedPremium}</Text>
        </View>
      )}
      <View className={couponStyles.couponRow()}>
        <View className="flex-1">
          <View className={couponStyles.couponFieldWrapper()}>
            <TextInput
              value={couponCode}
              onChangeText={onCouponCodeChange}
              placeholder={t('sharedFlows_common:orderSummary.couponCode')}
              placeholderTextColor="#9CA3AF"
              className={couponStyles.couponInput()}
              style={{ borderWidth: 0 }}
            />
          </View>
          {error && <Text className={couponStyles.couponError()}>{error}</Text>}
        </View>
        <Pressable onPress={onApplyCoupon} disabled={!couponCode || isCouponPending}>
          <View className={couponStyles.couponButton()}>
            <Text className={body2({ color: couponCode ? 'white' : 'grey500', bold: true })}>
              {t('sharedFlows_common:orderSummary.apply')}
            </Text>
          </View>
        </Pressable>
      </View>
      {showPermanentDiscountNotice ? (
        <Text className={couponStyles.couponFloatingLabel({ className: 'mt-2' })}>
          {t('sharedFlows_common:orderSummary.couponReplacesPermanentDiscount')}
        </Text>
      ) : null}
    </>
  )
}

export { orderSummaryStyles }
export type { CouponData }
